import { addHandlers, addProps, flowMax, SimplePropsAdder } from 'ad-hok'
import { branchIfNullish } from 'ad-hok-utils'
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
  useSearchParams,
} from 'react-router-dom'

type AddParam = <TParamName extends string>(
  name: TParamName
) => <TProps>(props: TProps) => TProps & { [param in TParamName]: string }

export const addPathParam: AddParam = (param) =>
  addProps(() => ({
    [param]: useParams()[param],
  })) as any

type AddOptionalParam = <TParamName extends string>(
  name: TParamName
) => <TProps>(props: TProps) => TProps & { [param in TParamName]?: string }

export const addOptionalPathParam: AddOptionalParam = addPathParam

export const addOptionalQueryParam: AddOptionalParam = (param: string) =>
  flowMax(
    addProps(() => ({
      [param]: useSearchParams()[0].get(param),
    }))
  ) as any

type AddIntParam = <TParamName extends string>(
  name: TParamName
) => <TProps>(props: TProps) => TProps & { [param in TParamName]: number }

export const addIntPathParam: AddIntParam = (param) =>
  flowMax(
    addProps(() => ({
      [param]: useParams()[param],
    })),
    addProps(({ [param]: value }) => ({
      [param]: value ? Number.parseInt(value) : undefined,
    }))
  ) as any

type AddOptionalIntParam = <TParamName extends string>(
  name: TParamName
) => <TProps>(props: TProps) => TProps & { [param in TParamName]?: number }

export const addOptionalIntPathParam: AddOptionalIntParam = addIntPathParam

export const addOptionalIntQueryParam: AddOptionalIntParam = (param: string) =>
  flowMax(
    addOptionalQueryParam(param),
    addProps(({ [param]: value }) => ({
      [param]: value ? Number.parseInt(value) : undefined,
    }))
  ) as any

type AddLocation = SimplePropsAdder<{
  location: Location
}>

export const addLocation: AddLocation = addProps(() => ({
  location: useLocation(),
}))

type AddNavigate = SimplePropsAdder<{
  navigate: NavigateFunction
}>

export const addNavigate: AddNavigate = addProps(() => ({
  navigate: useNavigate(),
}))

const normalizeUrlHash = (hash: string): string =>
  !hash ? '' : hash.startsWith('#') ? hash : `#${hash}`

const getSynchronouslyUpToDateNavigationState = (): Record<string, any> =>
  window.history.state?.usr ?? {}

interface UpdateCurrentHistoryEntryOptions {
  additionalState?: Record<string, any>
  hash?: string
}

type AddUpdateCurrentHistoryEntry = SimplePropsAdder<{
  updateCurrentHistoryEntry: (opts: UpdateCurrentHistoryEntryOptions) => void
}>

export const addUpdateCurrentHistoryEntry: AddUpdateCurrentHistoryEntry =
  flowMax(
    addNavigate,
    addLocation,
    addProps(({ location: { pathname, hash } }) => ({
      pathname,
      hash,
    })),
    addHandlers(
      {
        updateCurrentHistoryEntry:
          ({ navigate, pathname, hash: locationHash }) =>
          ({ additionalState, hash }: UpdateCurrentHistoryEntryOptions) => {
            navigate(
              `${pathname}${
                hash != null ? normalizeUrlHash(hash) : locationHash
              }`,
              {
                replace: true,
                state: {
                  ...getSynchronouslyUpToDateNavigationState(),
                  ...(additionalState ?? {}),
                },
              }
            )
          },
      },
      ['pathname', 'hash', 'navigate']
    )
  )

type RedirectTo404IfNullish = <
  TProps extends {},
  TPropName extends keyof TProps
>(
  propNames: Array<TPropName> | TPropName
) => (props: TProps) => TProps &
  Required<{
    [propName in TPropName]: NonNullable<TProps[propName]>
  }>

export const redirectTo404IfNullish: RedirectTo404IfNullish = (propNames) =>
  branchIfNullish(propNames, { returns: () => <Navigate to="/" /> })
